<!-- src/views/OnBoarding/EmployeeInductionFormView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0]">
    <FormHeader />
    <div class="p-4 sm:p-6 md:p-8">
      <div class="max-w-7xl mx-auto">
        
        <!-- Premium Header Card -->
        <div class="bg-[#0A1630] rounded-t-[40px] p-8 border-b-4 border-[#20FCF1] shadow-2xl">
          <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 class="text-3xl font-black text-white uppercase tracking-tight">Employee Induction Form</h1>
              <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-2">
                Compliance & Policy Acknowledgement • Sentinel Safe Premium
              </p>
            </div>
            <div class="bg-white/5 px-4 py-2 rounded-xl border border-white/10 flex items-center gap-3">
              <div class="w-2 h-2 bg-[#20FCF1] rounded-full"></div>
              <span class="text-white text-[10px] font-black uppercase tracking-widest">Official Record</span>
            </div>
          </div>
        </div>

        <div class="bg-white p-6 sm:p-10 rounded-b-[40px] shadow-xl border-x border-b border-slate-200">
          
          <!-- Dynamic Introductory Text -->
          <section 
            v-if="isLoadingText || (mainTextContent && mainTextContent !== '<p><br></p>')" 
            class="mb-12 p-8 bg-slate-50 rounded-3xl border border-slate-200 shadow-inner"
          >
            <div v-if="isLoadingText" class="text-slate-400 animate-pulse flex items-center gap-2">
               <div class="w-4 h-4 bg-slate-200 rounded-full"></div>
               <span class="font-black uppercase text-xs">Loading Induction Instructions...</span>
            </div>
            <div v-else class="ql-editor !p-0 text-slate-700" v-html="mainTextContent"></div>
          </section>

          <form @submit.prevent="submitForm" class="space-y-16">

            <!-- Section 1 - Personnel Snapshot -->
            <section>
              <div class="flex items-center gap-4 mb-8">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <IdentificationIcon class="h-6 w-6" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">1. Personnel Details</h2>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-8 bg-slate-50 p-8 rounded-[32px] border border-slate-100">
                <div class="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                  <label class="form-label text-slate-400">Full Name</label>
                  <p class="text-lg font-black text-slate-800 uppercase tracking-tight">{{ userProfile.name || 'Loading...' }}</p>
                </div>
                <div class="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                  <label class="form-label text-slate-400">Verified Email</label>
                  <p class="text-lg font-black text-slate-800 lowercase tracking-tight">{{ userProfile.email || 'Loading...' }}</p>
                </div>
              </div>
            </section>

            <!-- Section 2 - Induction Details -->
            <section>
              <div class="flex items-center gap-4 mb-8">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <CalendarDaysIcon class="h-6 w-6" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">2. Induction Session</h2>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-8 p-8 bg-slate-50 rounded-[32px] border border-slate-100">
                <div>
                  <label for="inductionDate" class="form-label">Induction Date <span class="text-red-500">*</span></label>
                  <input type="date" id="inductionDate" v-model="formData.inductionDate" required class="form-input" />
                </div>
                <div>
                  <label for="inductionLocation" class="form-label">Induction Location <span class="text-red-500">*</span></label>
                   <select id="inductionLocation" v-model="formData.inductionLocation" required class="form-input">
                    <option disabled value="">
                       {{ inductionLocationOptions.length > 0 ? 'Select Location' : 'Loading Locations...' }}
                    </option>
                    <option v-for="option in inductionLocationOptions" :key="option.value" :value="option.value">
                      {{ option.text }}
                    </option>
                  </select>
                </div>
              </div>
            </section>

            <!-- Section 3 - Agreements -->
            <section>
              <div class="flex items-center gap-4 mb-8">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <ClipboardDocumentCheckIcon class="h-6 w-6" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">3. Brochure Agreements</h2>
              </div>

              <div class="space-y-4">
                <div class="AgreementCard">
                  <input id="agreeCodeOfConduct" type="checkbox" v-model="formData.agreeCodeOfConduct" class="agreement-checkbox">
                  <label for="agreeCodeOfConduct" class="ml-4 block text-sm font-bold text-slate-700">
                    I have read, understand and agree to the 
                    <a href="/onboarding-material#code-of-conduct" target="_blank" class="text-brand-accent hover:underline decoration-2 font-black uppercase text-xs ml-1">Code of Conduct</a>.
                    <span class="text-red-500 ml-1">*</span>
                  </label>
                </div>

                <div class="AgreementCard">
                  <input id="agreePolicies" type="checkbox" v-model="formData.agreePolicies" class="agreement-checkbox">
                  <label for="agreePolicies" class="ml-4 block text-sm font-bold text-slate-700">
                    I have read, understand and agree to follow the company 
                    <a href="/onboarding-material#tecs-policies" target="_blank" class="text-brand-accent hover:underline decoration-2 font-black uppercase text-xs ml-1">Policies</a>.
                    <span class="text-red-500 ml-1">*</span>
                  </label>
                </div>

                <div class="AgreementCard">
                  <input id="agreeProcedures" type="checkbox" v-model="formData.agreeProcedures" class="agreement-checkbox">
                  <label for="agreeProcedures" class="ml-4 block text-sm font-bold text-slate-700">
                    I have read, understand and agree to follow the company 
                    <a href="/onboarding-material#tecs-procedures" target="_blank" class="text-brand-accent hover:underline decoration-2 font-black uppercase text-xs ml-1">Procedures</a>.
                    <span class="text-red-500 ml-1">*</span>
                  </label>
                </div>

                <div class="AgreementCard">
                  <input id="agreeSponsorContract" type="checkbox" v-model="formData.agreeSponsorContract" class="agreement-checkbox">
                  <label for="agreeSponsorContract" class="ml-4 block text-sm font-bold text-slate-700">
                    I have read, understand and agree to the 
                    <a href="/onboarding-material#sponsor-contract" target="_blank" class="text-brand-accent hover:underline decoration-2 font-black uppercase text-xs ml-1">Primary Sponsor Contract</a>.
                    <span class="text-red-500 ml-1">*</span>
                  </label>
                </div>
              </div>
            </section>

            <!-- Section 4 - Acknowledgements -->
            <section>
              <div class="flex items-center gap-4 mb-8">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <ShieldCheckIcon class="h-6 w-6" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">4. Acknowledgements</h2>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div class="AcknowledgementBox">
                   <input id="acknowledgement1" type="checkbox" v-model="formData.acknowledgement1" class="agreement-checkbox">
                   <label for="acknowledgement1" class="ml-4 block text-sm font-bold text-slate-700">I acknowledge my responsibility to adhere to all company rules and policies. <span class="text-red-500">*</span></label>
                </div>
                <div class="AcknowledgementBox">
                   <input id="acknowledgement2" type="checkbox" v-model="formData.acknowledgement2" class="agreement-checkbox">
                   <label for="acknowledgement2" class="ml-4 block text-sm font-bold text-slate-700">I acknowledge that I have received all information and understand the company expectations. <span class="text-red-500">*</span></label>
                </div>
              </div>
            </section>

            <!-- Section 5 - Final Confirmation -->
            <section class="bg-slate-900 p-10 rounded-[40px] text-white shadow-xl relative overflow-hidden">
                <div class="absolute top-0 right-0 p-10 opacity-5">
                  <CheckBadgeIcon class="h-32 w-32" />
                </div>
                <div class="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
                    <div class="max-w-xl">
                      <h2 class="text-2xl font-black text-[#20FCF1] uppercase tracking-tight mb-2">5. Final Confirmation</h2>
                      <p class="text-slate-400 font-medium">By ticking this box, you formally declare that all information provided is accurate and truthful.</p>
                    </div>
                    <div class="bg-white/5 p-6 rounded-[32px] border border-white/10">
                      <div class="flex items-center">
                        <input id="confirmation" type="checkbox" v-model="formData.confirmation" class="h-10 w-10 text-[#20FCF1] border-white/20 rounded-xl bg-transparent">
                        <label for="confirmation" class="ml-4 block text-xs font-black uppercase tracking-widest text-white leading-tight">Confirm &<br>Attest Records <span class="text-red-500">*</span></label>
                      </div>
                    </div>
                </div>
            </section>
            
            <!-- Section 6 - Signature Preview -->
            <section>
              <div class="flex items-center gap-4 mb-8">
                <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
                  <PencilSquareIcon class="h-6 w-6" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight">6. Digital Signature</h2>
              </div>

              <div class="bg-slate-50 p-8 rounded-[32px] border border-slate-100">
                  <div class="max-w-md mx-auto">
                    <label class="form-label text-slate-400 text-center mb-6">Profile Linked Signature</label>
                    <div class="bg-white p-8 rounded-[32px] border-2 border-dashed border-slate-200 shadow-inner flex flex-col items-center">
                        <div v-if="userProfile.signatureUrl || userProfile.signatureDataUrl" class="animate-fadeIn">
                            <img :src="userProfile.signatureUrl || userProfile.signatureDataUrl" alt="Signature Preview" class="max-h-24 object-contain" />
                            <div class="mt-6 flex items-center justify-center gap-2 bg-[#EBFDF8] px-4 py-2 rounded-full border border-[#20FCF1]/20">
                              <CheckBadgeIcon class="h-4 w-4 text-[#20FCF1]" />
                              <span class="text-[10px] font-black uppercase text-[#0A1630] tracking-widest">Verified Profile Signature</span>
                            </div>
                        </div>
                        <div v-else class="text-center space-y-4">
                            <div class="bg-red-50 p-4 rounded-xl text-red-600 text-xs font-bold uppercase tracking-tight border border-red-100">
                                Signature not found in your profile.
                            </div>
                            <button @click="$router.push({ name: 'yourDetails' })" type="button" class="px-6 py-2 bg-[#0A1630] text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 transition-all">
                                Update Details First
                            </button>
                        </div>
                    </div>
                  </div>
              </div>
            </section>

            <!-- Submit Button Area -->
            <div class="pt-10">
              <button type="submit" :disabled="isSubmitting || !isFormValid" class="btn-premium">
                <span v-if="isSubmitting" class="flex items-center gap-3">
                    <svg class="animate-spin h-6 w-6" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                    Submitting Induction...
                </span>
                <span v-else>Submit Completed Induction Form</span>
              </button>
              
              <div v-if="successMessage" class="mt-8 p-6 bg-mint-50 border border-brand-accent rounded-3xl text-center shadow-lg animate-bounce">
                <p class="text-brand-primary font-black uppercase tracking-widest">{{ successMessage }}</p>
              </div>
              <div v-if="errorMessage" class="mt-8 p-6 bg-red-50 border border-red-200 rounded-3xl text-center font-bold text-red-700">
                {{ errorMessage }}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue';
import { useAuth } from '@/composables/useAuth';
import { db, firebaseApp } from '@/firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useRouter } from 'vue-router';
import FormHeader from '@/components/FormHeader.vue';

// Premium Icons
import { 
  IdentificationIcon, 
  CalendarDaysIcon, 
  ClipboardDocumentCheckIcon, 
  ShieldCheckIcon, 
  CheckBadgeIcon, 
  PencilSquareIcon 
} from '@heroicons/vue/24/outline';

const router = useRouter();
const { user } = useAuth();
const functions = getFunctions(firebaseApp, 'europe-west2');

const userProfile = reactive({ name: '', email: '', signatureUrl: '', signatureDataUrl: '' });
const inductionLocationOptions = ref([]);

const mainTextContent = ref('');
const isLoadingText = ref(true);

const formData = reactive({
  inductionDate: '',
  inductionLocation: '',
  agreeCodeOfConduct: false,
  agreePolicies: false,
  agreeProcedures: false,
  agreeSponsorContract: false,
  acknowledgement1: false,
  acknowledgement2: false,
  confirmation: false,
});

const successMessage = ref('');
const errorMessage = ref('');
const isSubmitting = ref(false);

const isFormValid = computed(() => {
    return !isLoadingText.value &&
           formData.inductionDate &&
           formData.inductionLocation &&
           formData.agreeCodeOfConduct &&
           formData.agreePolicies &&
           formData.agreeProcedures &&
           formData.agreeSponsorContract &&
           formData.acknowledgement1 &&
           formData.acknowledgement2 &&
           formData.confirmation &&
           (userProfile.signatureUrl || userProfile.signatureDataUrl);
});

const fetchPrerequisites = async () => {
  if (!user.value) {
    errorMessage.value = "You must be logged in to complete this form.";
    isLoadingText.value = false;
    return;
  }

  try {
    isLoadingText.value = true;
    
    // 1. Get Claims for Client ID
    const idTokenResult = await user.value.getIdTokenResult();
    const clientId = idTokenResult.claims.clientId;
    if (!clientId) throw new Error("Client ID missing.");

    // 2. References
    const userProfileRef = doc(db, 'userProfiles', user.value.uid);
    const clientDocRef = doc(db, 'clients', clientId);
    const optionsRef = doc(db, 'formOptions', 'inductionLocations');

    const [userProfileSnap, clientDocSnap, optionsSnap] = await Promise.all([
      getDoc(userProfileRef),
      getDoc(clientDocRef),
      getDoc(optionsRef)
    ]);

    // Process Profile
    if (userProfileSnap.exists()) {
      const profile = userProfileSnap.data();
      userProfile.name = `${profile.firstName || ''} ${profile.lastName || ''}`.trim();
      userProfile.email = profile.email || user.value.email;
      userProfile.signatureUrl = profile.signatureUrl || '';
      userProfile.signatureDataUrl = profile.signatureDataUrl || '';
    } else {
      throw new Error("Please complete the 'Your Details' form first.");
    }

    // Process Custom Text
    if (clientDocSnap.exists()) {
        mainTextContent.value = clientDocSnap.data().onboardingFormTexts?.employeeInduction || '';
    }

    // Process Dropdown Options
    if (optionsSnap.exists()) {
        const data = optionsSnap.data();
        const options = data[clientId] || data.global || [];
        inductionLocationOptions.value = options.sort((a, b) => a.text.localeCompare(b.text));
    }
    
  } catch (error) {
    errorMessage.value = `Error loading form content: ${error.message}`;
  } finally {
    isLoadingText.value = false;
  }
};

onMounted(fetchPrerequisites);

const submitForm = async () => {
  if (!isFormValid.value) {
    errorMessage.value = "Please fill out all required fields and acknowledge all statements.";
    return;
  }
  
  isSubmitting.value = true;
  errorMessage.value = '';
  successMessage.value = '';

  const submissionData = {
    ...formData,
    name: userProfile.name,
    email: userProfile.email,
    signatureUrl: userProfile.signatureUrl,
    signatureDataUrl: userProfile.signatureDataUrl,
    introductoryText: mainTextContent.value,
    status: 'completed',
  };

  try {
    const submitFormFunction = httpsCallable(functions, 'submitGenericForm');
    const result = await submitFormFunction({
      formIdentifier: 'employeeInduction',
      targetCollection: 'onboardingFormSubmissions',
      submissionData: submissionData
    });

    if (result.data.success) {
      successMessage.value = `Form submitted successfully! Reference: ${result.data.referenceNumber}`;
      setTimeout(() => router.push('/onboarding-forms'), 2000);
    } else {
      throw new Error(result.data.error || 'Submission failed.');
    }
  } catch (error) {
    errorMessage.value = `Failed to submit form: ${error.message}`;
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped>
.form-input { 
  @apply block w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-700 shadow-sm transition-all focus:ring-2 focus:ring-[#20FCF1] focus:border-[#20FCF1] sm:text-sm; 
}
.form-label { 
  @apply block text-[11px] font-black text-[#0A1630] uppercase tracking-[0.2em] mb-2; 
}
.btn-premium { 
  @apply w-full bg-[#20FCF1] text-[#0A1630] py-6 rounded-[32px] text-xl font-black uppercase tracking-[0.2em] shadow-2xl hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50 disabled:grayscale flex justify-center items-center; 
}

.AgreementCard, .AcknowledgementBox {
  @apply flex items-start p-6 bg-slate-50 border border-slate-100 rounded-3xl transition-all hover:bg-white hover:border-[#20FCF1]/30 hover:shadow-md;
}

.agreement-checkbox {
  @apply h-6 w-6 text-[#0A1630] border-slate-300 rounded-lg focus:ring-[#20FCF1] mt-0.5;
}

.animate-fadeIn {
  animation: fadeIn 0.5s ease-out forwards;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Quill Styling */
:deep(.ql-editor) {
  font-family: inherit;
  font-size: 0.95rem;
  line-height: 1.8;
  color: #334155;
}
:deep(.ql-editor p) {
  margin-bottom: 1.25rem;
}
:deep(.ql-editor h2) {
  @apply text-xl font-black uppercase tracking-tight text-slate-900 mt-8 mb-4;
}
</style>